'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import CCPALink from '@/components/CCPALink';
import ConsentPreferenceCenter from '@/components/ConsentPreferenceCenter';
import { Shield } from 'lucide-react';

export default function LandingFooter() {
  const [showPreferences, setShowPreferences] = useState(false);

  return (
    <>
      <footer className="bg-teal-900 text-teal-100" aria-label="Site footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <AppLogo size={32} />
                <span className="font-display font-bold text-xl text-white">SafePlay</span>
              </div>
              <p className="text-sm text-teal-300 leading-relaxed mb-4">
                ID-verified gaming for families. Safe, trusted, and fun — on every platform your kids love.
              </p>
              <div className="flex items-center gap-1.5 text-xs text-teal-400">
                <Shield size={12} aria-hidden="true" />
                <span>COPPA &amp; GDPR Compliant</span>
              </div>
            </div>

            {[
              {
                id: 'footer-product',
                title: 'Product',
                links: ['Platform Dashboard', 'Parent Controls', 'Message Boards', 'Tournaments', 'Pricing'],
              },
              {
                id: 'footer-company',
                title: 'Company',
                links: ['About Us', 'Blog', 'Careers', 'Press', 'Contact'],
              },
              {
                id: 'footer-legal',
                title: 'Legal',
                links: ['About Us', 'Blog', 'Careers', 'Press', 'Contact'],
              },
            ]?.map((col) => (
              <nav key={col?.id} aria-label={`${col?.title} links`}>
                <h3 className="font-display font-semibold text-white text-sm mb-4">{col?.title}</h3>
                <ul className="space-y-2.5">
                  {col?.links?.map((link) => (
                    <li key={`${col?.id}-${link}`}>
                      <a
                        href="#"
                        className="text-sm text-teal-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
                      >
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>

          <div className="border-t border-teal-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-teal-400">© 2026 SafePlay Inc. All rights reserved.</p>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link
                href="/privacy"
                className="text-xs text-teal-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
              >
                Privacy Policy
              </Link>
              <Link
                href="/cookies"
                className="text-xs text-teal-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
              >
                Cookie Policy
              </Link>
              <Link
                href="/accessibility"
                className="text-xs text-teal-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
              >
                Accessibility
              </Link>
              <CCPALink />
              <button
                onClick={() => setShowPreferences(true)}
                className="text-xs text-teal-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
              >
                Cookie Preferences
              </button>
              <Link
                href="/sign-up-login-screen"
                className="text-xs text-teal-300 hover:text-white transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
              >
                Sign In
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="text-xs bg-amber-500 hover:bg-amber-400 text-white font-semibold px-4 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 focus:ring-offset-teal-900"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </footer>
      {showPreferences && (
        <ConsentPreferenceCenter
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
        />
      )}
    </>
  );
}